import { ConsultantNode, PayoutBreakdown } from '../types/career';
import { MonthlySimulationStep, SimulationResult } from '../types/simulation';
import { evaluateTreeQualifications, evaluateCareerLevel } from '../math/qualification';
import { calculateNodePayout, DEFAULT_USD_EXCHANGE_RATE } from '../math/payout';
import { cloneTree, countActiveConsultants, countTotalConsultants, findNodeById } from '../utils/treeUtils';

let sandboxNodeCounter = 100;

/**
 * Generates a unique node ID for sandbox nodes.
 */
export function generateSandboxNodeId(): string {
  return `sb-${++sandboxNodeCounter}`;
}

/**
 * Adds a new subordinate consultant node to a specified parent in the tree.
 * Automatically recalculates cumulative metrics and promotions.
 */
/**
 * Bottom-up post-order recalculation of a sandbox node and its subtree:
 * 1. Post-order: Recurse into all children first.
 * 2. Monthly turnover (Місячний обіг):
 *    - Sums up from consultants to leader!
 *    - teamMonthlyAE = sum of children's totalMonthlyAE
 *    - totalMonthlyAE = (isActive ? personalMonthlyAE : 0) + teamMonthlyAE
 * 3. Cumulative units (Накопичені одиниці):
 *    - Independent: do NOT sum from children!
 *    - Preserves personalCumulativeAE and teamCumulativeAE as set by user.
 *    - totalCumulativeAE = personalCumulativeAE + teamCumulativeAE
 * 4. Career levels:
 *    - Evaluates career level strictly using evaluateCareerLevel, guaranteeing
 *      that Level 3 strictly requires >= 700 total AE AND >= 300 personal AE.
 */
export function recalculateSandboxNode(node: ConsultantNode): void {
  // 1. Recurse into children first (bottom-up)
  for (const child of node.children) {
    recalculateSandboxNode(child);
  }

  // 2. Monthly turnover: sum from consultants to leader
  const childrenMonthlyTurnover = node.children.reduce(
    (sum, c) => sum + (c.totalMonthlyAE ?? 0),
    0
  );
  node.teamMonthlyAE = childrenMonthlyTurnover;
  const effectivePersonalMonthly = node.isActive ? (node.personalMonthlyAE ?? 0) : 0;
  node.totalMonthlyAE = Math.round((effectivePersonalMonthly + childrenMonthlyTurnover) * 10) / 10;

  // 3. Cumulative units: preserve independent personal and team units (do NOT sum from children)
  if (node.personalCumulativeAE === undefined) {
    node.personalCumulativeAE = 0;
  }
  if (node.teamCumulativeAE === undefined) {
    node.teamCumulativeAE = Math.max(0, (node.totalCumulativeAE ?? 0) - node.personalCumulativeAE);
  }
  node.totalCumulativeAE = Math.round((node.personalCumulativeAE + node.teamCumulativeAE) * 10) / 10;

  // 4. Strict career level qualification based on units and direct branches
  const directBranchLevels = node.children.map((c) => c.level);
  const qualifiedFromScratch = evaluateCareerLevel(
    1,
    node.totalCumulativeAE,
    node.personalCumulativeAE,
    directBranchLevels
  );

  // If node in constructor has level >= 3 but < 700 total AE or < 300 personal AE,
  // strictly enforce qualified level (cannot hold Level 3 without 700 total and 300 personal).
  if (node.level >= 3 && (node.totalCumulativeAE < 700 || node.personalCumulativeAE < 300)) {
    node.level = qualifiedFromScratch;
  } else if (node.level === 2 && node.totalCumulativeAE < 100) {
    node.level = qualifiedFromScratch;
  } else {
    node.level = evaluateCareerLevel(
      node.level,
      node.totalCumulativeAE,
      node.personalCumulativeAE,
      directBranchLevels
    );
  }
}

export const normalizeSandboxTree = recalculateSandboxNode;
export const normalizeSandboxNode = recalculateSandboxNode;

/**
 * Adds a new subordinate consultant node to a specified parent in the tree.
 * Monthly turnover is summed up to the leader; cumulative units remain independent.
 */
export function addNode(
  root: ConsultantNode,
  parentId: string,
  nodeData?: Partial<ConsultantNode>
): { root: ConsultantNode; newNode: ConsultantNode } {
  const treeClone = cloneTree(root);
  const parent = findNodeById(treeClone, parentId);

  if (!parent) {
    throw new Error(`Parent node with ID "${parentId}" not found in structure.`);
  }

  const newId = nodeData?.id || generateSandboxNodeId();
  const childIndex = parent.children.length + 1;
  const initialPersonalCum = nodeData?.personalCumulativeAE ?? 50;
  const initialTeamCum = nodeData?.teamCumulativeAE ?? 0;
  const initialPersonalMon = nodeData?.personalMonthlyAE ?? initialPersonalCum;
  const defaultName = parent.parentId === null ? `Гілка ${childIndex}` : `Консультант ${childIndex}`;

  const newNode: ConsultantNode = {
    id: newId,
    name: nodeData?.name || defaultName,
    parentId: parent.id,
    level: nodeData?.level || 1,
    personalCumulativeAE: initialPersonalCum,
    personalMonthlyAE: initialPersonalMon,
    teamCumulativeAE: initialTeamCum,
    teamMonthlyAE: 0,
    totalCumulativeAE: initialPersonalCum + initialTeamCum,
    totalMonthlyAE: initialPersonalMon,
    monthlyMeetings: nodeData?.monthlyMeetings ?? 10,
    conversionRate: nodeData?.conversionRate ?? 0.20,
    recruitmentRate: nodeData?.recruitmentRate ?? 0.5,
    isActive: nodeData?.isActive ?? true,
    children: nodeData?.children ? nodeData.children.map(cloneTree) : [],
    customColor: nodeData?.customColor,
  };

  parent.children.push(newNode);
  recalculateSandboxNode(treeClone);

  return {
    root: treeClone,
    newNode,
  };
}

/**
 * Updates properties of an existing consultant node in the tree.
 * Monthly turnover is summed up to the leader; cumulative units remain independent.
 */
export function updateNode(
  root: ConsultantNode,
  id: string,
  partialData: Partial<ConsultantNode>
): ConsultantNode {
  const treeClone = cloneTree(root);
  const node = findNodeById(treeClone, id);

  if (!node) {
    throw new Error(`Node with ID "${id}" not found in structure.`);
  }

  // Update properties
  Object.assign(node, partialData);

  // If monthly meetings or conversion rate updated, sync personalMonthlyAE if active
  if (
    (partialData.monthlyMeetings !== undefined || partialData.conversionRate !== undefined) &&
    partialData.personalMonthlyAE === undefined &&
    node.isActive
  ) {
    const contracts = node.monthlyMeetings * node.conversionRate;
    node.personalMonthlyAE = Math.round(contracts * 50 * 10) / 10;
  }

  recalculateSandboxNode(treeClone);

  return treeClone;
}

/**
 * Deletes a consultant node and its entire subtree from the organization.
 * Root node cannot be deleted.
 */
export function deleteNode(root: ConsultantNode, id: string): ConsultantNode {
  if (root.id === id) {
    throw new Error('Cannot delete the root leader node of the structure.');
  }

  const treeClone = cloneTree(root);

  function removeChildFromParent(current: ConsultantNode): boolean {
    const idx = current.children.findIndex((c) => c.id === id);
    if (idx !== -1) {
      current.children.splice(idx, 1);
      return true;
    }
    for (const child of current.children) {
      if (removeChildFromParent(child)) {
        return true;
      }
    }
    return false;
  }

  removeChildFromParent(treeClone);
  recalculateSandboxNode(treeClone);
  return treeClone;
}

/**
 * Toggles a consultant's active / dormant status.
 * Inactive consultants produce 0 personal work, but retain their place in the hierarchy (non-destructive).
 */
export function toggleNodeActive(root: ConsultantNode, id: string): ConsultantNode {
  const treeClone = cloneTree(root);
  const node = findNodeById(treeClone, id);

  if (!node) {
    throw new Error(`Node with ID "${id}" not found in structure.`);
  }

  node.isActive = !node.isActive;

  if (!node.isActive) {
    (node as any).savedPersonalMonthlyAE = node.personalMonthlyAE || (node as any).savedPersonalMonthlyAE || 50;
    node.personalMonthlyAE = 0;
  } else {
    node.personalMonthlyAE =
      (node as any).savedPersonalMonthlyAE ||
      Math.round(((node.monthlyMeetings || 10) * (node.conversionRate || 0.20)) * 50 * 10) / 10 ||
      50;
  }

  recalculateSandboxNode(treeClone);

  return treeClone;
}

/**
 * Recalculates structural metrics and financial payout for a sandbox tree.
 * Monthly turnover is summed from consultants to leader.
 * Cumulative units preserve user-entered independent values.
 */
export function recalculateSandboxTree(
  root: ConsultantNode,
  usdExchangeRate: number = DEFAULT_USD_EXCHANGE_RATE,
  commissionRateCoefficient: number = 0.8
): { root: ConsultantNode; payout: PayoutBreakdown } {
  const treeClone = cloneTree(root);
  recalculateSandboxNode(treeClone);
  const payout = calculateNodePayout(treeClone, usdExchangeRate, commissionRateCoefficient);
  return { root: treeClone, payout };
}

/**
 * Runs a multi-period forecast (1–60 months) over an arbitrary custom sandbox tree.
 */
export function simulateSandboxForecast(
  initialRoot: ConsultantNode,
  months: number = 12,
  usdExchangeRate: number = DEFAULT_USD_EXCHANGE_RATE
): SimulationResult {
  const totalMonths = Math.max(1, Math.min(60, months));
  const workingTree = cloneTree(initialRoot);

  function initializeBaselines(node: ConsultantNode) {
    for (const child of node.children) {
      initializeBaselines(child);
    }
    const childrenTotalAE = node.children.reduce((sum, c) => sum + (c.totalCumulativeAE ?? 0), 0);
    const userTeamAE = node.teamCumulativeAE ?? 0;
    node.baselineTeamCumulativeAE = Math.max(0, userTeamAE - childrenTotalAE);
    node.teamCumulativeAE = node.baselineTeamCumulativeAE + childrenTotalAE;
    node.totalCumulativeAE = Math.round(((node.personalCumulativeAE ?? 0) + node.teamCumulativeAE) * 10) / 10;
  }
  initializeBaselines(workingTree);

  const steps: MonthlySimulationStep[] = [];

  const recruitPoolMap = new Map<string, number>();

  for (let month = 1; month <= totalMonths; month++) {
    // 1. Process personal monthly production for all consultants
    function processProduction(node: ConsultantNode) {
      if (node.isActive) {
        const contracts = (node.monthlyMeetings || 10) * (node.conversionRate || 0.20);
        node.personalMonthlyAE = Math.round(contracts * 50 * 10) / 10;
        node.personalCumulativeAE = Math.round((node.personalCumulativeAE + node.personalMonthlyAE) * 10) / 10;
      } else {
        node.personalMonthlyAE = 0;
      }

      for (const child of node.children) {
        processProduction(child);
      }
    }

    processProduction(workingTree);

    // 2. Process organic recruitment based on recruitmentRate
    function processRecruitment(node: ConsultantNode) {
      if (node.isActive && node.recruitmentRate > 0) {
        const pool = (recruitPoolMap.get(node.id) || 0) + node.recruitmentRate;
        const newCount = Math.floor(pool);
        recruitPoolMap.set(node.id, pool - newCount);

        for (let i = 0; i < newCount; i++) {
          const newId = generateSandboxNodeId();
          const newNode: ConsultantNode = {
            id: newId,
            name: `Партнер ${node.name.replace(/[^0-9]/g, '') || ''}.${node.children.length + 1}`,
            parentId: node.id,
            level: 1,
            personalCumulativeAE: 50,
            personalMonthlyAE: 50,
            totalCumulativeAE: 50,
            totalMonthlyAE: 50,
            monthlyMeetings: 10,
            conversionRate: 0.20,
            recruitmentRate: Math.max(0.2, node.recruitmentRate * 0.5),
            isActive: true,
            joinedMonth: month,
            children: [],
            baselineTeamCumulativeAE: 0,
          };
          node.children.push(newNode);
        }
      }

      for (const child of node.children) {
        processRecruitment(child);
      }
    }

    processRecruitment(workingTree);

    // 3. Evaluate qualifications
    const evaluated = evaluateTreeQualifications(workingTree);
    function syncEvaluated(target: ConsultantNode, source: ConsultantNode) {
      target.level = source.level;
      target.teamMonthlyAE = source.teamMonthlyAE;
      target.teamCumulativeAE = source.teamCumulativeAE;
      target.totalMonthlyAE = source.totalMonthlyAE;
      target.totalCumulativeAE = source.totalCumulativeAE;
      for (let i = 0; i < target.children.length; i++) {
        if (source.children[i]) {
          syncEvaluated(target.children[i], source.children[i]);
        }
      }
    }
    syncEvaluated(workingTree, evaluated);

    // 4. Calculate Payout
    const payoutBreakdown = calculateNodePayout(workingTree, usdExchangeRate);

    // 5. Record Step
    steps.push({
      month,
      rootConsultant: cloneTree(workingTree),
      payoutBreakdown,
      activeConsultantsCount: countActiveConsultants(workingTree),
      totalConsultantsCount: countTotalConsultants(workingTree),
      totalStructuralTurnoverAE: workingTree.totalMonthlyAE,
      monthlyPersonalAE: workingTree.personalMonthlyAE,
      cumTotalAE: workingTree.totalCumulativeAE,
      cumPersonalAE: workingTree.personalCumulativeAE,
    });
  }

  const lastStep = steps[steps.length - 1];

  return {
    steps,
    totalMonths,
    finalLevel: lastStep.rootConsultant.level,
    finalMonthlyIncomeUAH: lastStep.payoutBreakdown.totalGrossIncomeUAH,
    finalMonthlyIncomeUSD: lastStep.payoutBreakdown.totalGrossIncomeUSD,
    finalTeamSize: lastStep.totalConsultantsCount,
    finalActivePartners: lastStep.activeConsultantsCount,
    finalCumulativeAE: lastStep.cumTotalAE,
  };
}
